import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { compareDesc } from 'date-fns';
import { parseDate } from '@/lib/localizedDateUtils';

export type TimelineEventType = 
  | 'contact' 
  | 'promise' 
  | 'promise_fulfilled' 
  | 'promise_broken' 
  | 'payment' 
  | 'payment_due' 
  | 'agreement';

export interface TimelineEvent {
  id: string;
  type: TimelineEventType;
  date: string;
  title: string;
  description: string | null;
  metadata: Record<string, unknown>;
}

export function useClientTimeline(contactId: string) {
  return useQuery({
    queryKey: ['client-timeline', contactId],
    queryFn: async () => {
      const events: TimelineEvent[] = [];

      // Fetch collection history
      const { data: collectionHistory, error: chError } = await supabase
        .from('collection_history')
        .select('*')
        .eq('contact_id', contactId)
        .order('contact_date', { ascending: false });

      if (chError) throw chError;

      // Add collection history events
      for (const entry of collectionHistory || []) {
        // Contact event
        events.push({
          id: `contact-${entry.id}`,
          type: 'contact',
          date: entry.contact_date,
          title: getContactTitle(entry.contact_type, entry.outcome),
          description: entry.notes,
          metadata: {
            contact_type: entry.contact_type,
            outcome: entry.outcome,
            reason: entry.reason,
          },
        });

        // Promise event (if applicable)
        if (entry.outcome === 'promised' && entry.promised_date) {
          events.push({
            id: `promise-${entry.id}`,
            type: 'promise',
            date: entry.contact_date,
            title: `Prometeu pagar em ${formatDateBR(entry.promised_date)}`,
            description: entry.promised_amount 
              ? `Valor: ${formatCurrency(entry.promised_amount)}` 
              : null,
            metadata: {
              promised_date: entry.promised_date,
              promised_amount: entry.promised_amount,
            },
          });
        }
      }

      // Fetch payments for this contact via deals
      const { data: deals } = await supabase
        .from('deals')
        .select('id')
        .eq('contact_id', contactId);

      if (deals && deals.length > 0) {
        const dealIds = deals.map(d => d.id);
        const { data: payments } = await supabase
          .from('payments')
          .select('id, amount, status, due_date, paid_at')
          .in('deal_id', dealIds);

        if (payments) {
          for (const payment of payments) {
            // Payment completed event
            if (payment.status === 'paid' && payment.paid_at) {
              events.push({
                id: `payment-${payment.id}`,
                type: 'payment',
                date: payment.paid_at,
                title: 'Pagamento recebido',
                description: formatCurrency(payment.amount),
                metadata: {
                  amount: payment.amount,
                },
              });
            }

            // Overdue payment event
            if (payment.status === 'pending' && payment.due_date) {
              const dueDate = parseDate(payment.due_date);
              if (dueDate && dueDate < new Date()) {
                events.push({
                  id: `due-${payment.id}`,
                  type: 'payment_due',
                  date: payment.due_date,
                  title: 'Parcela vencida',
                  description: formatCurrency(payment.amount),
                  metadata: {
                    amount: payment.amount,
                    payment_id: payment.id,
                  },
                });
              }
            }
          }
        }

        // Fetch payment agreements for deals
        const { data: agreements, error: aError } = await supabase
          .from('payment_agreements')
          .select('*')
          .in('deal_id', dealIds);

        if (!aError && agreements) {
          for (const agreement of agreements) {
            events.push({
              id: `agreement-${agreement.id}`,
              type: 'agreement',
              date: agreement.created_at,
              title: getAgreementTitle(agreement.status),
              description: agreement.reason_notes,
              metadata: {
                status: agreement.status,
                agreement_number: agreement.agreement_number,
                original_balance: agreement.original_balance,
                agreed_amount: agreement.agreed_amount,
                num_installments: agreement.num_installments,
                approved_at: agreement.approved_at,
                rejection_reason: agreement.rejection_reason,
              },
            });
          }
        }
      }

      // Sort by date descending
      events.sort((a, b) => compareDesc(parseDate(a.date) || new Date(0), parseDate(b.date) || new Date(0)));

      return events;
    },
    enabled: !!contactId,
  });
}

function getContactTitle(contactType: string, outcome: string): string {
  const typeLabels: Record<string, string> = {
    whatsapp: 'WhatsApp',
    call: 'Ligação',
    email: 'Email',
    in_person: 'Presencial',
  };

  const outcomeLabels: Record<string, string> = {
    no_answer: 'Não atendeu',
    promised: 'Prometeu pagar',
    refused: 'Recusou',
    negotiating: 'Negociando',
    partial_payment: 'Pagamento parcial',
    paid: 'Confirmou pagamento',
    renegotiated: 'Renegociou',
    wrong_number: 'Número errado',
    callback_requested: 'Pediu retorno',
  };

  return `${typeLabels[contactType] || contactType}: ${outcomeLabels[outcome] || outcome}`;
}

function getAgreementTitle(status: string): string {
  const labels: Record<string, string> = {
    pending_approval: 'Renegociação solicitada',
    approved: 'Renegociação aprovada',
    rejected: 'Renegociação rejeitada',
    active: 'Acordo ativo',
    completed: 'Acordo concluído',
    defaulted: 'Acordo inadimplente',
    superseded: 'Acordo substituído',
    cancelled: 'Acordo cancelado',
  };
  return labels[status] || 'Acordo de pagamento';
}

function formatDateBR(dateStr: string): string {
  const date = parseDate(dateStr);
  return date ? date.toLocaleDateString('pt-BR') : '-';
}

function formatCurrency(value: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}
